import { IconButton, Link } from '@montugroup/design-system';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Collapse, Stack, Toolbar, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useState } from 'react';

import { menus } from '@/constants/menu';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';

import settings from '@/constants/constants';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import AdminMenuV2 from './navbar/AdminTileV2';
import NavbarMenuV2 from './navbar/DashboardMenuTileV2';

const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  width: '6.5rem',
  height: '1.6rem',
  flexShrink: 0,
});

const StyledAppBar = styled(AppBar)({
  boxShadow: 'none',
  width: '100%',
});

const DesktopMenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
}));

const AdminContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
});

const MobileIconButton = styled(IconButton)({
  '&:focus': {
    outline: 'none',
  },
});

const MobileMenuStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  paddingTop: '4.5rem',
  gap: '1rem',
  paddingLeft: '0.5rem',
  paddingBottom: '1.5rem',
  width: '100%',
}));

const MobileAdminContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  marginTop: '1.5rem',
  width: '100%',
});

const SupportLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.white,
  marginLeft: theme.spacing(6),
  '&:hover': {
    color: theme.palette.common.white,
    textDecoration: 'none',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: theme.spacing(6),
  },
}));

function Navbar() {
  const { Logo } = usePortalPartnerBrand();
  const { data: user } = useUser();
  const { zendeskSupportURL } = settings;
  const [menuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const renderDesktopMenu = () => (
    <>
      <DesktopMenuContainer>
        {menus.map((menu) => (
          <NavbarMenuV2
            key={menu.id}
            name={menu.name}
            link={menu.link}
            featureFlag={menu.featureFlag}
            activeReferal={false}
          />
        ))}
        <SupportLink href={zendeskSupportURL} target="_blank" rel="noopener" underline="none">
          Support
        </SupportLink>
      </DesktopMenuContainer>
      <AdminContainer>{user && <AdminMenuV2 firstName={user.firstName} />}</AdminContainer>
    </>
  );

  const renderMobileMenu = () => (
    <Collapse in={menuOpen} timeout="auto" unmountOnExit>
      <MobileMenuStack>
        {user &&
          menus.map((menu) => (
            <NavbarMenuV2
              key={menu.id}
              name={menu.name}
              link={menu.link}
              featureFlag={menu.featureFlag}
              activeReferal={false}
            />
          ))}
        <SupportLink href={zendeskSupportURL} target="_blank" rel="noopener" underline="none">
          Support
        </SupportLink>
        {user && (
          <MobileAdminContainer>
            <AdminMenuV2 firstName={user.firstName} />
          </MobileAdminContainer>
        )}
      </MobileMenuStack>
    </Collapse>
  );

  return (
    <>
      <StyledAppBar position="fixed">
        <Toolbar>
          <LogoContainer sx={{ marginLeft: '1.5rem' }}>
            <Logo fillColor={theme.palette.secondary.main} sx={{ width: '6.5rem' }} />
          </LogoContainer>

          {!isMobileView && user && renderDesktopMenu()}

          {isMobileView && (
            <Stack sx={{ alignItems: 'center', marginLeft: 'auto' }}>
              <MobileIconButton edge="start" color="inherit" aria-label="menu" onClick={toggleMenu}>
                <MenuIcon
                  sx={{
                    color: theme.palette.secondary.main,
                    fontSize: '2rem',
                    marginRight: '0.5rem',
                  }}
                />
              </MobileIconButton>
            </Stack>
          )}
        </Toolbar>
      </StyledAppBar>
      {isMobileView && renderMobileMenu()}
    </>
  );
}

export default Navbar;
